import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { usePushNotifications } from '../../shared/lib/usePushNotifications'
import PushConnectionBanner from './PushConnectionBanner'
import { openBellNotification, markAllNotificationsRead } from '../../shared/lib/notificationBell'
import '../../shared/ui/HeaderPanels.css'

export default function NotificationBell() {
    const { token, user } = useAuth()
    const businessUser = ['master', 'mechanic', 'ros', 'master_workshop'].includes(user?.role)
    const navigate = useNavigate()
    const { status: pushStatus, error: pushError, requestPermission } = usePushNotifications(businessUser ? token : null)
    const [open, setOpen] = useState(false)
    const [tab, setTab] = useState('unread')
    const [unread, setUnread] = useState([])
    const [history, setHistory] = useState([])
    const [historyTotal, setHistoryTotal] = useState(0)
    const [historyOffset, setHistoryOffset] = useState(0)
    const [loading, setLoading] = useState(false)
    const [readingAll, setReadingAll] = useState(false)
    const [now, setNow] = useState(() => Date.now())

    const fetchNotifications = useCallback(async () => {
        if (!token) return
        try {
            const res = await fetch('/api/notifications', {
                headers: { Authorization: `Bearer ${token}` }
            })
            const data = await res.json()
            if (!res.ok || localStorage.getItem('token') !== token) return
            setNow(Date.now())
            setUnread(data.unread || [])
            setHistory(data.history || [])
            setHistoryTotal(data.historyTotal || 0)
            setHistoryOffset(data.history?.length || 0)
        } catch { /* Keep the last loaded notification state on a network error. */ }
    }, [token])

    useEffect(() => {
        let disposed = false
        Promise.resolve().then(() => { if (!disposed) fetchNotifications() })
        const interval = setInterval(fetchNotifications, 30000)
        return () => { disposed = true; clearInterval(interval) }
    }, [fetchNotifications])

    useEffect(() => {
        const close = () => setOpen(false)
        document.addEventListener('click', close)
        document.addEventListener('close-all-panels', close)
        return () => {
            document.removeEventListener('click', close)
            document.removeEventListener('close-all-panels', close)
        }
    }, [])

    useEffect(() => {
        if (!token) return
        let socket
        let reconnectTimeout
        let isUnmounted = false

        const connect = () => {
            const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
            socket = new WebSocket(`${protocol}://${window.location.host}/api/ws?token=${token}`)

            socket.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data)
                    if (data.type === 'orders_changed' || data.type === 'notifications_changed') fetchNotifications()
                } catch { /* Keep the last loaded notification state on a network error. */ }
            }

            socket.onclose = () => {
                if (isUnmounted) return
                reconnectTimeout = setTimeout(connect, 5000)
            }
        }

        connect()

        return () => {
            isUnmounted = true
            clearTimeout(reconnectTimeout)
            if (socket) socket.close()
        }
    }, [token, fetchNotifications])

    const openNotification = notification => openBellNotification(notification, {
        token,
        close: () => setOpen(false),
        navigate,
        onRecorded: () => {
            setUnread(prev => prev.filter(n => n.id !== notification.id))
            fetchNotifications()
        },
    })

    const readAll = async () => {
        if (readingAll) return
        setReadingAll(true)
        try {
            if (!await markAllNotificationsRead(token)) return
            setUnread([])
            fetchNotifications()
        } finally { setReadingAll(false) }
    }

    const loadMore = async () => {
        if (loading) return
        setLoading(true)
        try {
            const res = await fetch(`/api/notifications/history?offset=${historyOffset}&limit=20`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            const data = await res.json()
            setHistory(prev => [...prev, ...(data.history || [])])
            setHistoryOffset(prev => prev + (data.history?.length || 0))
        } catch { /* Keep the last loaded notification state on a network error. */ }
        setLoading(false)
    }

    const formatTime = (dateStr) => {
        const diff = Math.floor((now - new Date(dateStr)) / 1000)
        if (diff < 60) return 'только что'
        if (diff < 3600) return `${Math.floor(diff / 60)} мин назад`
        if (diff < 86400) return `${Math.floor(diff / 3600)} ч назад`
        return new Date(dateStr).toLocaleDateString('ru-RU', { timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit' })
    }

    const unreadCount = unread.length
    const canLoadMore = historyOffset < Math.min(historyTotal, 50)

    return (
        <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
            <div onClick={() => {
                if (!open) document.dispatchEvent(new CustomEvent('close-all-panels'))
                setOpen(p => !p)
            }} style={{ position: 'relative', width: '32px', height: '32px', cursor: 'pointer' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="ti ti-bell" style={{ fontSize: '16px', color: '#6B7280' }} />
                </div>
                {unreadCount > 0 && (
                    <div style={{ position: 'absolute', top: '-2px', right: '-2px', minWidth: '16px', height: '16px', background: '#DC2626', borderRadius: '50%', border: '2px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '600', color: 'white', padding: '0 3px' }}>
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </div>
                )}
            </div>

            {open && (
                <div className="hp-panel hp-notifications">
                    <div className="hp-head">
                        <p className="hp-title">Уведомления</p>
                        {tab === 'unread' && unread.length > 0 && <button onClick={readAll} disabled={readingAll} className="hp-link" style={{ cursor: readingAll ? 'wait' : 'pointer' }}>{readingAll ? 'Отмечаем…' : 'Прочитать все'}</button>}
                    </div>
                    <div className="hp-tabs">
                        <button onClick={() => setTab('unread')} aria-pressed={tab === 'unread'}>Новые {unreadCount > 0 && <span className="hp-count">{unreadCount}</span>}</button>
                        <button onClick={() => setTab('history')} aria-pressed={tab === 'history'}>История</button>
                    </div>

                    {businessUser && <PushConnectionBanner status={pushStatus} error={pushError} onConnect={requestPermission} />}

                    {tab === 'unread' && (
                        unread.length === 0 ? (
                            <div className="hp-empty">
                                <div className="hp-empty-icon"><i className="ti ti-bell-check" aria-hidden="true" /></div>
                                <p>Новых уведомлений нет</p>
                            </div>
                        ) : (
                            <div className="hp-list">
                                {unread.map(n => (
                                    <div key={n.id} className="hp-item" data-unread="true"
                                        onClick={() => openNotification(n)}>
                                        <span className="hp-item-icon"><i className="ti ti-bell-ringing" aria-hidden="true" /></span>
                                        <div className="hp-item-text">
                                            <p className="hp-item-title">{n.title}</p>
                                            <p className="hp-item-body">{n.body}</p>
                                            <p className="hp-item-time">{formatTime(n.created_at)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    )}

                    {tab === 'history' && (
                        history.length === 0 ? (
                            <div className="hp-empty">
                                <div className="hp-empty-icon"><i className="ti ti-history" aria-hidden="true" /></div>
                                <p>История пуста</p>
                            </div>
                        ) : (
                            <div className="hp-list">
                                {history.map(n => (
                                    <div key={n.id} className="hp-item" data-unread="false"
                                        onClick={() => openNotification(n)}>
                                        <span className="hp-item-icon"><i className="ti ti-bell" aria-hidden="true" /></span>
                                        <div className="hp-item-text">
                                            <p className="hp-item-title">{n.title}</p>
                                            <p className="hp-item-body">{n.body}</p>
                                            <p className="hp-item-time">{formatTime(n.created_at)}</p>
                                        </div>
                                    </div>
                                ))}
                                {canLoadMore && (
                                    <button onClick={loadMore} disabled={loading} className="hp-more">
                                        {loading ? 'Загрузка...' : `Загрузить ещё · осталось ${Math.min(historyTotal, 50) - historyOffset}`}
                                    </button>
                                )}
                                <div className="hp-foot">Показываем последние 50 уведомлений</div>
                            </div>
                        )
                    )}
                </div>
            )}
        </div>
    )
}
